// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Explanatory diagrams for the framework docs.
//
// Art direction, so these stay consistent as more are added:
//
//   A diagram earns its place by showing what prose shows badly - containment,
//   ordering, spatial relationship. Prose is better at nuance, so the caption
//   carries the caveats and the drawing carries the shape. If a diagram needs a
//   legend it is too complex and should be two diagrams or a sentence.
//
//   ONE accent per diagram, doing exactly one job: marking the thing being
//   explained. Everything else is neutral. Colour is never the only signal -
//   position and label say it too, so it survives being printed, and being read
//   by someone who cannot separate the hues.
//
//   Flat. No gradients, no shadows, no isometric, no curves - orthogonal
//   connectors only. Nothing here imitates the nxt editor's own chrome:
//   recreating an application's UI in SVG ages badly and promises a fidelity
//   nobody is going to maintain.
//
//   Monospace for anything that is a name in the system, which ties the
//   drawings to the <code> styling already on the page.
//
// Drawn against a fixed viewBox and scaled to the column width, so they hold
// together on a phone without a horizontal scroller. Every one carries a title
// and an accessible name, because a diagram with no text equivalent is just an
// image of an explanation.
// ==========================================================================

import React from 'react';

const ACCENT = 'var(--color-brand-link)';
const LINE = 'var(--color-text-muted-alt)';
const FAINT = 'var(--color-text-muted-light)';
const INK = 'var(--color-text-primary)';
const MUTED = 'var(--color-text-secondary)';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const Figure: React.FC<{
  caption: string;
  label: string;
  viewBox: string;
  height: number;
  children: React.ReactNode;
}> = ({ caption, label, viewBox, height, children }) => (
  <figure className="my-[26px]">
    <div className="rounded-[12px] border border-border-dark/[0.11] bg-surface-section px-[16px] py-[18px]">
      <svg
        role="img"
        aria-label={label}
        viewBox={viewBox}
        style={{ width: '100%', height: 'auto', maxHeight: height }}
        preserveAspectRatio="xMidYMid meet"
      >
        <title>{label}</title>
        {children}
      </svg>
    </div>
    <figcaption className="mt-[9px] text-[12.5px] leading-[1.6] text-content-muted-alt">
      {caption}
    </figcaption>
  </figure>
);

/** A node: attributes above, code below, execution flowing left to right. */
export const NodeDiagram: React.FC = () => (
  <Figure
    label="An nxt node holds attributes and Python, and passes execution to the next node"
    caption="A node holds attributes and a block of Python. Execution passes from one to the next; attributes resolve as tokens inside the code."
    viewBox="0 0 520 150"
    height={170}
  >
    {[0, 300].map((x, index) => (
      <g key={x} transform={`translate(${x}, 18)`}>
        <rect
          width="190"
          height="112"
          rx="8"
          fill="var(--color-bg-card)"
          stroke={index === 0 ? ACCENT : LINE}
          strokeWidth={index === 0 ? 1.5 : 1}
        />
        <text x="12" y="21" fontSize="12" fontFamily={MONO} fill={INK}>
          {index === 0 ? '/build/load' : '/build/prebuild'}
        </text>
        <line x1="0" y1="32" x2="190" y2="32" stroke={LINE} strokeWidth="1" opacity="0.45" />

        {/* Two representative attributes, elided. Showing more would be
            drawing a spreadsheet rather than explaining a node. */}
        <text x="12" y="52" fontSize="11" fontFamily={MONO} fill={MUTED}>
          model
        </text>
        <text x="96" y="52" fontSize="11" fontFamily={MONO} fill={FAINT}>
          ${'{'}asset{'}'}.fbx
        </text>
        <text x="12" y="70" fontSize="11" fontFamily={MONO} fill={MUTED}>
          scale
        </text>
        <text x="96" y="70" fontSize="11" fontFamily={MONO} fill={FAINT}>
          1.0
        </text>

        <line x1="0" y1="80" x2="190" y2="80" stroke={LINE} strokeWidth="1" opacity="0.45" />
        <text x="12" y="99" fontSize="11" fontFamily={MONO} fill={MUTED}>
          import maya.cmds as mc
        </text>
      </g>
    ))}

    <line x1="190" y1="74" x2="300" y2="74" stroke={LINE} strokeWidth="1.5" />
    <path d="M292 69 L300 74 L292 79 Z" fill={LINE} />
    <text x="245" y="64" fontSize="10.5" textAnchor="middle" fill={FAINT}>
      execute
    </text>
  </Figure>
);

/** Layers: a small graph on top of the ones it inherits. */
export const LayersDiagram: React.FC = () => {
  const layers = [
    { name: 'biped_default_rig', nodes: '15 nodes', top: true },
    { name: 'face_rig  +  body_rig', nodes: '322 + 94 nodes', top: false },
    { name: 'anim_rig', nodes: '201 nodes', top: false },
    { name: 'rig_bits · utils · delivery', nodes: '625 nodes', top: false }
  ];
  return (
    <Figure
      label="A small template graph opens on top of the larger graphs it inherits"
      caption="The graph you open is the thin one on top. It inherits everything beneath and overrides only what it needs, which is why a whole character rig is a fifteen-node file."
      viewBox="0 0 520 210"
      height={230}
    >
      {layers.map((layer, index) => {
        const y = 14 + index * 48;
        // The top layer is inset, so "sits on top of and is smaller than" is
        // read from the shape before the labels are read at all.
        const inset = layer.top ? 60 : 0;
        return (
          <g key={layer.name} transform={`translate(${20 + inset}, ${y})`}>
            <rect
              width={400 - inset * 2}
              height="36"
              rx="7"
              fill={layer.top ? 'var(--color-bg-card)' : 'var(--color-bg-base)'}
              stroke={layer.top ? ACCENT : LINE}
              strokeWidth={layer.top ? 1.5 : 1}
              opacity={layer.top ? 1 : 0.85}
            />
            <text x="14" y="23" fontSize="12" fontFamily={MONO} fill={layer.top ? INK : MUTED}>
              {layer.name}
            </text>
            <text
              x={400 - inset * 2 - 14}
              y="23"
              fontSize="10.5"
              textAnchor="end"
              fill={FAINT}
            >
              {layer.nodes}
            </text>
          </g>
        );
      })}
      <text x="446" y="36" fontSize="10.5" fill={ACCENT}>
        you open
      </text>
      <text x="446" y="150" fontSize="10.5" fill={FAINT}>
        inherited
      </text>
      <line x1="440" y1="60" x2="440" y2="196" stroke={LINE} strokeWidth="1" opacity="0.5" />
    </Figure>
  );
};

/** Instancing: right is left, with the side attributes flipped. */
export const InstanceDiagram: React.FC = () => (
  <Figure
    label="The right side is an instance of the left with its side attributes flipped"
    caption="right is an instance of left, not a copy. It takes the same children and overrides only the side attributes, so anything added on the left appears on the right with no second copy to keep in step."
    viewBox="0 0 520 178"
    height={195}
  >
    <g transform="translate(24, 20)">
      <rect width="180" height="120" rx="8" fill="var(--color-bg-card)" stroke={LINE} />
      <text x="14" y="24" fontSize="12" fontFamily={MONO} fill={INK}>
        /left
      </text>
      {['arm', 'leg', 'foot'].map((child, index) => (
        <g key={child} transform={`translate(24, ${40 + index * 24})`}>
          <rect width="132" height="18" rx="4" fill="var(--color-bg-base)" stroke={LINE} opacity="0.8" />
          <text x="9" y="13" fontSize="10.5" fontFamily={MONO} fill={MUTED}>
            {child}
          </text>
        </g>
      ))}
    </g>

    <g transform="translate(316, 20)">
      <rect width="180" height="120" rx="8" fill="var(--color-bg-card)" stroke={ACCENT} strokeWidth="1.5" />
      <text x="14" y="24" fontSize="12" fontFamily={MONO} fill={INK}>
        /right
      </text>
      {['arm', 'leg', 'foot'].map((child, index) => (
        <g key={child} transform={`translate(24, ${40 + index * 24})`}>
          <rect
            width="132"
            height="18"
            rx="4"
            fill="var(--color-bg-base)"
            stroke={LINE}
            strokeDasharray="3 3"
            opacity="0.8"
          />
          <text x="9" y="13" fontSize="10.5" fontFamily={MONO} fill={FAINT}>
            {child}
          </text>
        </g>
      ))}
    </g>

    <line x1="204" y1="72" x2="316" y2="72" stroke={ACCENT} strokeWidth="1.5" />
    <path d="M308 67 L316 72 L308 77 Z" fill={ACCENT} />
    <text x="260" y="62" fontSize="10.5" textAnchor="middle" fill={ACCENT}>
      instance of
    </text>
    {/* Named, not just implied by colour: the override is the whole point.
        Below the boxes rather than between them - the gap is 112 units wide and
        this label is wider than that, so in the gap it sat across both. */}
    <text x="260" y="166" fontSize="10.5" textAnchor="middle" fontFamily={MONO} fill={FAINT}>
      side · side_multiplier · colour overridden
    </text>
  </Figure>
);

/**
 * The build order, as a rail rather than a diagram.
 *
 * Nine stages in sequence is a list, and the page already has a table saying
 * what each one does. Drawing it as boxes would restate the order, lose the
 * descriptions, and not stay legible at this column width. So: order only,
 * sitting directly above the table that explains it.
 */
export const StageRail: React.FC = () => {
  const stages = [
    'init',
    'load',
    'prebuild',
    'center',
    'left',
    'right',
    'post_build',
    'apply',
    'delivery'
  ];
  return (
    <div className="my-[18px] flex flex-wrap items-center gap-x-[7px] gap-y-[8px]">
      {stages.map((stage, index) => (
        <React.Fragment key={stage}>
          <span className="font-mono text-[11.5px] px-[9px] py-[4px] rounded-[6px] bg-surface-section border border-border-dark/[0.12] text-content-secondary">
            {stage}
          </span>
          {index < stages.length - 1 && (
            <span aria-hidden="true" className="text-content-muted-alt text-[11px]">
              →
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
